import React, { useState, useEffect } from 'react';
import {
    X, FileText, Download, Monitor, ClipboardCheck, Check, Loader2,
    RotateCcw, Copy, AlertTriangle, Save, Edit3, Eye, Plus, Trash2,
    ArrowRight, ChevronLeft, ChevronRight, Mic, Globe, RefreshCcw, Calendar, Sparkles
} from 'lucide-react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';
import { voiceover } from '../services/api';
import { downloadFile } from '../utils/download';
import { trackEvent } from '../utils/telemetry';
import { stripMarkdown, normalizeBullets } from '../utils/richText';
import {
    trackPremiumAdoptionEvent,
    hasRecordedFirstAction,
    markFirstAction,
} from '../utils/telemetry';
import {
    buildGenerationReceiptDetails,
    getGenerationModePresentation,
} from '../utils/lessonReliability';
import QuizModal from './QuizModal';
import VoiceSetupModal from './VoiceSetupModal';
import VoiceoverModal from './VoiceoverModal';
import UpgradeNudge from './UpgradeNudge';
import PricingPage from './PricingPage';
import LessonChatPanel from './LessonChatPanel';

/**
 * PostGenerationView — Sprint 2, Full-page lesson review surface.
 *
 * Replaces LessonResultModal. Two-column desktop layout:
 *   LEFT  (70%) — editable lesson content, revision input, version toggle
 *   RIGHT (30%) — deliverables panel (6 buttons), Plan Tomorrow CTA
 *
 * Edit-first: deliverables disabled until lesson is saved.
 */
const PostGenerationView = ({
    result,
    userProfile,
    selectedSubject,
    gradeLevel,
    lessonDate,
    onClose,
    onPlanNextDay,
    onOpenClassroomBoard,
    onLessonSaved,
}) => {
    // ==================== STATE ====================

    // Save gating
    // Reopened lessons already have result.id — treat them as saved so deliverables
    // are immediately available without re-saving. Fresh generations have no id.
    const [isSaved, setIsSaved] = useState(() => !!result?.id);
    const [savingLesson, setSavingLesson] = useState(false);
    const [savedDocumentId, setSavedDocumentId] = useState(() => result?.id || null);

    // Per-button loading
    const [generatingSlides, setGeneratingSlides] = useState(false);
    const [generatingPacket, setGeneratingPacket] = useState(false);
    const [generatingQuiz, setGeneratingQuiz] = useState(false);
    const [downloadingPptx, setDownloadingPptx] = useState(false);
    const [exportingCanvas, setExportingCanvas] = useState(false);

    // Phase 3.2: Lesson Chat availability — resolved from backend-populated localStorage.
    // Initialized at mount (after loadFeatureFlags runs post-login).
    // Also listens for the 'storage' event so that:
    //   a) allowlisted users who just logged in see the panel appear immediately
    //      (loadFeatureFlags() fires async after handleLoginSuccess)
    //   b) tabs that are already open pick up the updated flag
    const _readLessonChatFlag = () => {
        try {
            const flags = JSON.parse(localStorage.getItem('saige_feature_flags') || '{}');
            return flags.lesson_chat === true;
        } catch {
            return false;
        }
    };
    const [lessonChatEnabled, setLessonChatEnabled] = useState(_readLessonChatFlag);

    useEffect(() => {
        const handleStorageChange = (event) => {
            if (!event.key || event.key === 'saige_feature_flags') {
                setLessonChatEnabled(_readLessonChatFlag());
            }
        };
        window.addEventListener('storage', handleStorageChange);
        // Re-read immediately on mount in case loadFeatureFlags() already completed
        // but ran before this component mounted (common on return-to-lesson flows)
        setLessonChatEnabled(_readLessonChatFlag());
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Quiz
    const [quizGenerated, setQuizGenerated] = useState(null);
    const [showQuizModal, setShowQuizModal] = useState(false);

    // Canvas
    const [canvasSuccess, setCanvasSuccess] = useState(false);

    // Voiceover
    const [generatingVoiceover, setGeneratingVoiceover] = useState(false);
    const [voiceoverReady, setVoiceoverReady] = useState(false);
    const [voiceoverAsset, setVoiceoverAsset] = useState(null);
    const [showVoiceSetupModal, setShowVoiceSetupModal] = useState(false);
    const [showVoiceoverModal, setShowVoiceoverModal] = useState(false);

    // Error
    const [actionError, setActionError] = useState(null);

    // Edit mode
    const [editMode, setEditMode] = useState(false);
    const [editedData, setEditedData] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);

    // Versioning — simplified to Previous / Current
    const [currentVersion, setCurrentVersion] = useState(1);
    const [versions, setVersions] = useState([{ version: 1, revision_prompt: null }]);
    const [revisionFeedback, setRevisionFeedback] = useState('');
    const [revising, setRevising] = useState(false);
    const [revisionSuccess, setRevisionSuccess] = useState(false);

    // Pricing / upgrade
    const [showPricing, setShowPricing] = useState(false);
    const [pricingSource, setPricingSource] = useState('direct');

    // Derived
    const lessonContent = result?.text || result?.content || '';
    const lessonTitle = result?.title || `${selectedSubject} - ${lessonDate}`;
    const isAnyActionRunning = generatingSlides || generatingPacket || generatingQuiz || savingLesson || downloadingPptx || revising || exportingCanvas || generatingVoiceover;
    const generationReceipt = result?.data?.generation_receipt || result?.metadata?.generation_receipt || null;
    const receiptWarnings = generationReceipt?.warnings || [];
    const receiptMode = generationReceipt?.mode || result?.data?.generation_mode || null;
    const receiptModePresentation = getGenerationModePresentation(receiptMode);
    const receiptDetails = buildGenerationReceiptDetails(generationReceipt);

    // ==================== EFFECTS ====================

    useEffect(() => {
        if (result?.data) {
            setEditedData({
                objectives: [...(result.data.objectives || [])],
                language_objectives: [...(result.data.language_objectives || [])],
                lesson_flow: (result.data.lesson_flow || []).map(item => ({ ...item })),
                materials: [...(result.data.materials || [])],
                differentiation: result.data.differentiation || '',
                assessment: result.data.assessment || '',
                unit_name: result.data.unit_name || '',
                bell_ringer: result.data.bell_ringer || null,
            });
        }
    }, [result?.data]);

    useEffect(() => {
        if (result?.id) setSavedDocumentId(result.id);
    }, [result?.id]);

    // P0-B: Voiceover CTA persistence — hydrate voiceover state from canonical lesson doc ID
    // This ensures the CTA appears for reopened/revised lessons, not just newly saved ones.
    useEffect(() => {
        const docId = result?.id || null;
        if (!docId || !userProfile?.email) return;

        // Non-blocking: check if a voiceover asset already exists for this lesson
        voiceover.getVoiceover(docId, userProfile.email)
            .then(res => {
                if (res.data?.asset) {
                    setVoiceoverAsset(res.data.asset);
                    setVoiceoverReady(true);
                }
            })
            .catch(() => {
                // Silently ignore — voiceover check is best-effort
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result?.id]);

    // ==================== SAVE HANDLER ====================

    const handleSaveLesson = async () => {
        setSavingLesson(true);
        setActionError(null);

        try {
            const structuredData = editedData || result?.data || null;

            const saveResp = await api.post('/api/documents/save', {
                teacher_id: userProfile?.email,
                doc_type: 'lesson',
                title: lessonTitle,
                content: lessonContent,
                metadata: {
                    subject: selectedSubject,
                    grade_level: gradeLevel,
                    lesson_date: lessonDate,
                    saved_at: new Date().toISOString(),
                    lesson_id: result?.lesson_id,
                    lesson_data: structuredData ? {
                        lesson_flow: structuredData.lesson_flow || [],
                        objectives: structuredData.objectives || [],
                        materials: structuredData.materials || [],
                        unit_name: structuredData.unit_name || '',
                        differentiation: structuredData.differentiation || '',
                        assessment: structuredData.assessment || '',
                        generation_mode: result?.data?.generation_mode || null,
                        generation_receipt: result?.data?.generation_receipt || null,
                        template_info: result?.data?.template_info || null,
                        template_compliance: result?.data?.template_compliance || null,
                        quality_tier: result?.data?.quality_tier || null,
                        fallback_used: result?.data?.fallback_used || false,
                    } : null,
                    generation_mode: result?.data?.generation_mode || null,
                    generation_receipt: result?.data?.generation_receipt || null,
                }
            });

            if (saveResp.data?.id) setSavedDocumentId(saveResp.data.id);
            setIsSaved(true);
            setHasChanges(false);
            if (onLessonSaved) onLessonSaved();
            trackEvent('lesson_saved', { template_id: result?.data?.template_info?.template_name || '' });

            // Phase 3B: Track first enhanced/premium lesson
            const tier = result?.data?.quality_tier;
            if (tier && tier !== 'standard' && !hasRecordedFirstAction('enhanced_lesson')) {
                markFirstAction('enhanced_lesson');
                trackPremiumAdoptionEvent('first_enhanced_lesson_generated', {
                    quality_tier: tier,
                    lesson_document_id: saveResp.data?.id,
                });
            }
        } catch (err) {
            console.error('Save lesson error:', err);
            setActionError({
                action: 'save',
                message: 'Failed to save lesson. Please try again.',
                retryFn: handleSaveLesson
            });
        } finally {
            setSavingLesson(false);
        }
    };

    // ==================== DELIVERABLE HANDLERS ====================

    const handlePresentLesson = async () => {
        setGeneratingSlides(true);
        setActionError(null);
        try {
            const response = await api.post('/api/production/generate-slides', {
                lesson_content: lessonContent,
                lesson_data: result?.data || {},
                teacher_id: userProfile?.email,
                subject: selectedSubject,
                grade_level: gradeLevel
            });
            if (response.data?.slides) {
                if (savedDocumentId) {
                    api.patch(`/api/documents/${savedDocumentId}/metadata`, {
                        teacher_id: userProfile?.email,
                        metadata: { has_slides: true }
                    }).catch(err => console.warn('Failed to update slides flag:', err));
                }
                if (onOpenClassroomBoard) onOpenClassroomBoard(response.data);
                trackEvent('deliverable_generated', { type: 'slides' });
            } else {
                throw new Error('No slides returned from API');
            }
        } catch (err) {
            console.error('Slide generation error:', err);
            setActionError({ action: 'slides', message: 'Failed to generate slides.', retryFn: handlePresentLesson });
        } finally {
            setGeneratingSlides(false);
        }
    };

    const handleDownloadPptx = async () => {
        setDownloadingPptx(true);
        setActionError(null);
        try {
            const lessonData = editedData || result?.data || {};
            await downloadFile('/api/production/download-pptx', {
                lesson_data: {
                    lesson_flow: lessonData.lesson_flow || [],
                    objectives: lessonData.objectives || [],
                    unit_name: lessonData.unit_name || selectedSubject,
                    materials: lessonData.materials || [],
                    subject: selectedSubject,
                    metadata: { lesson_date: lessonDate, subject: selectedSubject, grade_level: gradeLevel }
                },
                title: lessonTitle
            }, `${lessonTitle.replace(/\s+/g, '_')}.pptx`);
        } catch (err) {
            console.error('PPTX download error:', err);
            setActionError({ action: 'pptx', message: 'Failed to download PowerPoint.', retryFn: handleDownloadPptx });
        } finally {
            setDownloadingPptx(false);
        }
    };

    const handleStudentPacket = async () => {
        setGeneratingPacket(true);
        setActionError(null);
        try {
            await downloadFile('/api/production/generate-student-packet', {
                lesson_content: lessonContent,
                subject: selectedSubject,
                grade_level: gradeLevel,
                output_format: 'docx',
                teacher_id: userProfile?.email,
                lesson_title: lessonTitle
            }, `Student_Packet_${selectedSubject}_${lessonDate}.docx`);
            if (savedDocumentId) {
                api.patch(`/api/documents/${savedDocumentId}/metadata`, {
                    teacher_id: userProfile?.email,
                    metadata: { has_packet: true }
                }).catch(err => console.warn('Failed to update packet flag:', err));
            }
        } catch (err) {
            console.error('Packet generation error:', err);
            const detail = err.message || 'Failed to generate student packet.';
            setActionError({ action: 'packet', message: detail, retryFn: handleStudentPacket });
        } finally {
            setGeneratingPacket(false);
        }
    };

    const handleGenerateQuiz = async () => {
        if (!lessonContent || lessonContent.trim().length < 20) {
            setActionError({ action: 'quiz', message: 'Generate a lesson first before creating a quiz.', retryFn: handleGenerateQuiz });
            return;
        }
        setGeneratingQuiz(true);
        setActionError(null);
        try {
            const response = await api.post('/api/production/generate-assessment', {
                lesson_content: lessonContent,
                subject: selectedSubject,
                grade_level: gradeLevel,
                num_questions: 10,
                teacher_id: userProfile?.email,
                lesson_title: lessonTitle
            });
            const quizData = response.data;
            if (quizData?.questions?.length > 0) {
                setQuizGenerated(quizData);
                setShowQuizModal(true);
                trackEvent('deliverable_generated', { type: 'quiz' });
                if (savedDocumentId) {
                    api.patch(`/api/documents/${savedDocumentId}/metadata`, {
                        teacher_id: userProfile?.email,
                        metadata: { has_quiz: true }
                    }).catch(err => console.warn('Failed to update quiz flag:', err));
                }
            } else {
                setActionError({ action: 'quiz', message: 'Quiz generation returned incomplete data.', retryFn: handleGenerateQuiz });
            }
        } catch (err) {
            console.error('Quiz generation failed:', err);
            const detail = err.response?.data?.detail || err.message || 'Quiz generation unavailable — please try again.';
            setActionError({ action: 'quiz', message: detail, retryFn: handleGenerateQuiz });
        } finally {
            setGeneratingQuiz(false);
        }
    };

    const handleCopyForCanvas = async () => {
        if (!result?.lesson_id) return;
        setExportingCanvas(true);
        setActionError(null);
        try {
            const response = await api.post('/api/production/export-canvas', {
                lesson_id: result.lesson_id,
                version_number: currentVersion
            });
            if (response.data?.html) {
                await navigator.clipboard.writeText(response.data.html);
                setCanvasSuccess(true);
                setTimeout(() => setCanvasSuccess(false), 3000);
            } else {
                throw new Error('No HTML returned');
            }
        } catch (err) {
            console.error('Canvas export error:', err);
            setActionError({ action: 'canvas', message: 'Failed to export for Canvas.', retryFn: handleCopyForCanvas });
        } finally {
            setExportingCanvas(false);
        }
    };

    // ==================== VOICEOVER HANDLER ====================

    const handleGenerateVoiceover = async () => {
        setActionError(null);

        // Guard: lesson must be saved first (savedDocumentId is required)
        if (!savedDocumentId) {
            setActionError({
                action: 'voiceover',
                message: 'Please save the lesson first before generating a voiceover.',
            });
            return;
        }

        // If voiceover already exists, just open the modal with existing data
        if (voiceoverReady && voiceoverAsset) {
            setShowVoiceoverModal(true);
            return;
        }

        setGeneratingVoiceover(true);

        try {
            // 1. Check for existing voiceover first
            if (savedDocumentId) {
                const existingRes = await voiceover.getVoiceover(savedDocumentId, userProfile?.email);
                if (existingRes.data?.asset) {
                    setVoiceoverAsset(existingRes.data.asset);
                    setVoiceoverReady(true);
                    setShowVoiceoverModal(true);
                    setGeneratingVoiceover(false);
                    return;
                }
            }

            // 2. Check if teacher has a voice profile
            const voiceRes = await voiceover.getMyVoice(userProfile?.email);
            if (!voiceRes.data?.voice_profile) {
                // No voice profile — open setup modal instead
                setShowVoiceSetupModal(true);
                setGeneratingVoiceover(false);
                return;
            }

            // 3. Voice exists — open voiceover modal (will auto-generate script)
            setVoiceoverAsset(null);
            setShowVoiceoverModal(true);
            trackEvent('deliverable_generated', { type: 'voiceover_started' });
        } catch (err) {
            console.error('Voiceover flow error:', err);
            setActionError({
                action: 'voiceover',
                message: 'Failed to start voiceover flow. Please try again.',
                retryFn: handleGenerateVoiceover,
            });
        } finally {
            setGeneratingVoiceover(false);
        }
    };

    // ==================== REVISION HANDLERS ====================

    const handleReviseLesson = async () => {
        if (!revisionFeedback.trim() || !result?.lesson_id) return;
        setRevising(true);
        setActionError(null);
        setRevisionSuccess(false);
        try {
            const response = await api.post('/api/production/revise-lesson', {
                lesson_id: result.lesson_id,
                base_version: currentVersion,
                feedback: revisionFeedback.trim(),
                teacher_email: userProfile?.email
            });
            if (response.data?.lesson_json) {
                setEditedData({
                    objectives: [...(response.data.lesson_json.objectives || [])],
                    language_objectives: [...(response.data.lesson_json.language_objectives || [])],
                    lesson_flow: (response.data.lesson_json.lesson_flow || []).map(item => ({ ...item })),
                    materials: [...(response.data.lesson_json.materials || [])],
                    differentiation: response.data.lesson_json.differentiation || '',
                    assessment: response.data.lesson_json.assessment || '',
                    unit_name: response.data.lesson_json.unit_name || '',
                    bell_ringer: response.data.lesson_json.bell_ringer || null,
                });
                setCurrentVersion(response.data.version);
                setVersions(prev => [...prev, { version: response.data.version, revision_prompt: response.data.revision_prompt }]);
                setRevisionSuccess(true);
                setRevisionFeedback('');
                setIsSaved(false); // Edited → must re-save
                setTimeout(() => setRevisionSuccess(false), 3000);
            }
        } catch (err) {
            console.error('Revision error:', err);
            setActionError({ action: 'revise', message: err.response?.data?.detail || 'Failed to revise lesson.', retryFn: handleReviseLesson });
        } finally {
            setRevising(false);
        }
    };

    const handleSwitchVersion = async (versionNum) => {
        if (versionNum === currentVersion || !result?.lesson_id) return;
        try {
            const response = await api.get(`/api/production/lesson-version/${result.lesson_id}/${versionNum}`);
            if (response.data?.lesson_json) {
                setEditedData({
                    objectives: [...(response.data.lesson_json.objectives || [])],
                    language_objectives: [...(response.data.lesson_json.language_objectives || [])],
                    lesson_flow: (response.data.lesson_json.lesson_flow || []).map(item => ({ ...item })),
                    materials: [...(response.data.lesson_json.materials || [])],
                    differentiation: response.data.lesson_json.differentiation || '',
                    assessment: response.data.lesson_json.assessment || '',
                    unit_name: response.data.lesson_json.unit_name || '',
                    bell_ringer: response.data.lesson_json.bell_ringer || null,
                });
                setCurrentVersion(versionNum);
                setIsSaved(false);
            }
        } catch (err) {
            console.error('Switch version error:', err);
        }
    };

    // ==================== EDIT HANDLERS ====================

    const handleEditObjective = (index, newValue) => {
        const newObjectives = [...editedData.objectives];
        newObjectives[index] = newValue;
        setEditedData({ ...editedData, objectives: newObjectives });
        setHasChanges(true);
        setIsSaved(false);
    };

    const handleAddObjective = () => {
        setEditedData({ ...editedData, objectives: [...editedData.objectives, ''] });
        setHasChanges(true);
        setIsSaved(false);
    };

    const handleRemoveObjective = (index) => {
        setEditedData({ ...editedData, objectives: editedData.objectives.filter((_, i) => i !== index) });
        setHasChanges(true);
        setIsSaved(false);
    };

    const handleEditLessonFlow = (index, field, value) => {
        const newFlow = [...editedData.lesson_flow];
        newFlow[index] = { ...newFlow[index], [field]: value };
        setEditedData({ ...editedData, lesson_flow: newFlow });
        setHasChanges(true);
        setIsSaved(false);
    };

    const handleEditMaterials = (value) => {
        setEditedData({ ...editedData, materials: value.split('\n').filter(m => m.trim()) });
        setHasChanges(true);
        setIsSaved(false);
    };

    const handleEditField = (field, value) => {
        setEditedData({ ...editedData, [field]: value });
        setHasChanges(true);
        setIsSaved(false);
    };

    // Inline click-to-edit state — tracks which section is being edited
    const [inlineEditSection, setInlineEditSection] = useState(null);

    // Bell Ringer refresh — uses existing revise flow with scoped instruction
    const [refreshingBellRinger, setRefreshingBellRinger] = useState(false);
    const handleRefreshBellRinger = async () => {
        if (!result?.lesson_id) return;
        setRefreshingBellRinger(true);
        setActionError(null);
        try {
            const response = await api.post('/api/production/revise-lesson', {
                lesson_id: result.lesson_id,
                base_version: currentVersion,
                feedback: 'Generate a completely new bell ringer / warm-up activity. Keep everything else the same.',
                teacher_email: userProfile?.email
            });
            if (response.data?.lesson_json?.bell_ringer) {
                setEditedData(prev => ({
                    ...prev,
                    bell_ringer: response.data.lesson_json.bell_ringer,
                }));
                setIsSaved(false);
                setHasChanges(true);
            } else if (response.data?.lesson_json) {
                // Full revision came back — update everything
                setEditedData({
                    objectives: [...(response.data.lesson_json.objectives || [])],
                    language_objectives: [...(response.data.lesson_json.language_objectives || [])],
                    lesson_flow: (response.data.lesson_json.lesson_flow || []).map(item => ({ ...item })),
                    materials: [...(response.data.lesson_json.materials || [])],
                    differentiation: response.data.lesson_json.differentiation || '',
                    assessment: response.data.lesson_json.assessment || '',
                    unit_name: response.data.lesson_json.unit_name || '',
                    bell_ringer: response.data.lesson_json.bell_ringer || null,
                });
                setCurrentVersion(response.data.version);
                setVersions(prev => [...prev, { version: response.data.version, revision_prompt: 'Refreshed bell ringer' }]);
                setIsSaved(false);
            }
        } catch (err) {
            console.error('Bell ringer refresh error:', err);
            setActionError({ action: 'bell_ringer', message: 'Failed to refresh bell ringer.', retryFn: handleRefreshBellRinger });
        } finally {
            setRefreshingBellRinger(false);
        }
    };

    // Daily Agenda toggle
    const [showDailyAgenda, setShowDailyAgenda] = useState(false);

    // ==================== RENDER ====================

    if (!result) return null;

    // Deliverable button helper
    // eslint-disable-next-line no-unused-vars
    const DeliverableButton = ({ onClick, disabled, loading, icon: Icon, label, sublabel, success }) => (
        <button
            onClick={onClick}
            disabled={disabled || !isSaved}
            className={`flex items-center gap-3 w-full p-3.5 rounded-xl text-left transition-all duration-200 ${!isSaved
                ? 'bg-gray-50 text-gray-300 border border-gray-100 cursor-not-allowed'
                : success
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-white border border-slate-200 text-[#1d1d1f] hover:border-amber-300 hover:shadow-sm hover:scale-[1.01]'
                } disabled:opacity-60 disabled:cursor-not-allowed`}
        >
            {loading ? (
                <Loader2 size={18} className="animate-spin text-amber-500 flex-shrink-0" />
            ) : success ? (
                <Check size={18} className="flex-shrink-0" />
            ) : (
                <Icon size={18} className={`flex-shrink-0 ${isSaved ? 'text-[#777]' : 'text-gray-300'}`} />
            )}
            <div className="min-w-0">
                <span className="font-medium text-sm block truncate">{success ? `${label} ✓` : label}</span>
                <span className={`text-xs ${isSaved ? 'text-[#999]' : 'text-gray-300'}`}>{sublabel}</span>
            </div>
        </button>
    );

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen bg-[#f5f5f7]"
        >
            {/* ==================== HEADER BAR ==================== */}
            <div className="bg-white border-b border-slate-200/80 px-6 py-4 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                        <button
                            onClick={onClose}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <h1 className="text-lg font-semibold text-[#1d1d1f] truncate">{lessonTitle}</h1>
                                {receiptMode && (
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${receiptModePresentation.className}`}>
                                        {receiptModePresentation.label}
                                    </span>
                                )}
                                {/* Sprint 3 — Template badge */}
                                {result?.data?.template_info && (
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${result.data.template_info.is_district_enforced
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'bg-slate-100 text-slate-500'
                                        }`}>
                                        {result.data.template_info.is_district_enforced ? '🏛 ' : ''}
                                        {result.data.template_info.template_name || 'Saige Default'}
                                    </span>
                                )}
                                {/* Phase 1B — Quality tier badge (Pro/Premium only) */}
                                {result?.data?.quality_tier && result.data.quality_tier !== 'standard' && (
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
                                        result.data.quality_tier === 'escalation'
                                            ? 'bg-violet-100 text-violet-700'
                                            : 'bg-amber-100 text-amber-700'
                                    }`}>
                                        {result.data.quality_tier === 'escalation' ? '✦ Premium Quality' : '✦ Enhanced Quality'}
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-[#999]">{lessonDate} · {selectedSubject} · {gradeLevel}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Edit/View Toggle */}
                        {editedData && (
                            <button
                                onClick={() => setEditMode(!editMode)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${editMode
                                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                            >
                                {editMode ? <Eye size={16} /> : <Edit3 size={16} />}
                                {editMode ? 'Preview' : 'Edit'}
                            </button>
                        )}
                        {/* Save */}
                        <button
                            onClick={handleSaveLesson}
                            disabled={isAnyActionRunning || (isSaved && !hasChanges)}
                            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${isSaved && !hasChanges
                                ? 'bg-green-100 text-green-700 border border-green-300'
                                : 'bg-[#FFC107] text-black hover:bg-[#e6ad00]'
                                } disabled:opacity-50`}
                        >
                            {savingLesson ? <Loader2 size={16} className="animate-spin" /> : isSaved && !hasChanges ? <Check size={16} /> : <Save size={16} />}
                            {isSaved && !hasChanges ? 'Saved' : 'Save'}
                        </button>
                        {/* Regenerate */}
                        <button
                            onClick={onClose}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                        >
                            <RotateCcw size={16} />
                            New
                        </button>
                        {/* Close */}
                        <button
                            onClick={onClose}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>
            </div>

            {/* ==================== MAIN CONTENT ==================== */}
            <div className="max-w-7xl mx-auto px-6 py-6">
                {/* Error Banner */}
                <AnimatePresence>
                    {actionError && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center justify-between"
                        >
                            <div className="flex items-center gap-3 text-red-600">
                                <AlertTriangle size={18} />
                                <span className="text-sm">{actionError.message}</span>
                            </div>
                            <button
                                onClick={actionError.retryFn}
                                className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-600 rounded-lg text-sm hover:bg-red-200 transition-colors"
                            >
                                <RotateCcw size={14} /> Retry
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {generationReceipt && (
                    <div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-xl" data-testid="generation-receipt-panel">
                        <div className="flex items-start gap-3">
                            <AlertTriangle size={18} className="text-slate-500 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="text-sm font-medium text-slate-800">
                                        Generation details
                                    </p>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${receiptModePresentation.className}`}>
                                        {receiptModePresentation.label}
                                    </span>
                                </div>
                                <p className="text-xs text-slate-600 mt-1">
                                    {receiptModePresentation.description}
                                </p>

                                <div className="mt-3 grid sm:grid-cols-2 gap-2.5">
                                    {receiptDetails.items.map((item) => (
                                        <div key={item.key} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                                            <p className="text-[11px] uppercase tracking-wide text-slate-500">{item.label}</p>
                                            <p className="text-sm font-medium text-slate-800 mt-1">{item.value}</p>
                                            {item.detail && (
                                                <p className="text-[11px] text-slate-500 mt-1">{item.detail}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {receiptDetails.enrichmentIssues.length > 0 && (
                                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="generation-receipt-enrichment-issues">
                                        <p className="text-[11px] uppercase tracking-wide text-amber-700">Enrichment omitted</p>
                                        <div className="mt-1 flex flex-wrap gap-2">
                                            {receiptDetails.enrichmentIssues.map((issue) => (
                                                <span key={`${issue.stage}-${issue.status}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-white text-amber-700 border border-amber-200">
                                                    {issue.stage}: {issue.status}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {receiptWarnings.length > 0 && (
                                    <div className="mt-3 space-y-1" data-testid="generation-receipt-warnings">
                                        {receiptWarnings.map((warning, index) => (
                                            <p key={`${warning}-${index}`} className="text-xs text-slate-500">
                                                {warning}
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Sprint 3 — Template Compliance Warning (soft, dismissible) */}
                {result?.data?.template_compliance && !result.data.template_compliance.passed && (
                    <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                        <div className="flex items-start gap-3">
                            <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-amber-800">
                                    This lesson doesn't fully match your {result.data.template_info?.is_district_enforced ? 'district' : ''} template
                                </p>
                                {result.data.template_compliance.missing_sections?.length > 0 && (
                                    <p className="text-xs text-amber-600 mt-1">
                                        Missing sections: {result.data.template_compliance.missing_sections.join(', ')}
                                    </p>
                                )}
                                <p className="text-xs text-amber-500 mt-1">
                                    You can still save and use this lesson, or regenerate for better alignment.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex gap-6 items-start">
                    {/* ==================== LEFT COLUMN (70%) ==================== */}
                    <div className="flex-[7] min-w-0">
                        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
                            {/* Version toggle — simplified Previous / Current */}
                            {versions.length > 1 && (
                                <div className="px-6 pt-4 flex items-center gap-2">
                                    <button
                                        onClick={() => handleSwitchVersion(Math.max(1, currentVersion - 1))}
                                        disabled={currentVersion <= 1}
                                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-30 transition-colors"
                                    >
                                        <ChevronLeft size={14} /> Previous
                                    </button>
                                    <span className="text-xs text-slate-400">
                                        Version {currentVersion} of {versions.length}
                                    </span>
                                    <button
                                        onClick={() => handleSwitchVersion(Math.min(versions.length, currentVersion + 1))}
                                        disabled={currentVersion >= versions.length}
                                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-30 transition-colors"
                                    >
                                        Current <ChevronRight size={14} />
                                    </button>
                                </div>
                            )}

                            {/* Lesson Content */}
                            <div className="p-6">
                                {editMode && editedData ? (
                                    /* ===== Structured Edit View ===== */
                                    <div className="space-y-6">
                                        {/* Objectives */}
                                        <div className="bg-blue-50 rounded-xl p-4">
                                            <h4 className="text-sm font-semibold text-blue-700 mb-3 flex items-center gap-2">
                                                📌 Learning Objectives
                                                <button onClick={handleAddObjective} className="ml-auto p-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200"><Plus size={14} /></button>
                                            </h4>
                                            <div className="space-y-2">
                                                {editedData.objectives.map((obj, idx) => (
                                                    <div key={idx} className="flex items-start gap-2">
                                                        <input
                                                            type="text" value={obj}
                                                            onChange={(e) => handleEditObjective(idx, e.target.value)}
                                                            className="flex-1 px-3 py-2 text-sm rounded-lg border border-blue-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                                                            placeholder="Enter learning objective..."
                                                        />
                                                        <button onClick={() => handleRemoveObjective(idx)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Lesson Flow */}
                                        <div className="bg-amber-50 rounded-xl p-4">
                                            <h4 className="text-sm font-semibold text-amber-700 mb-3">⏱️ Lesson Flow</h4>
                                            <div className="space-y-3">
                                                {editedData.lesson_flow.map((step, idx) => (
                                                    <div key={idx} className="bg-white rounded-lg p-3 border border-amber-200">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <input type="text" value={step.time || ''} onChange={(e) => handleEditLessonFlow(idx, 'time', e.target.value)} className="w-20 px-2 py-1 text-xs rounded border border-amber-200 focus:border-amber-400 outline-none" placeholder="0-5" />
                                                            <input type="text" value={step.activity || ''} onChange={(e) => handleEditLessonFlow(idx, 'activity', e.target.value)} className="flex-1 px-2 py-1 text-sm font-medium rounded border border-amber-200 focus:border-amber-400 outline-none" placeholder="Activity name" />
                                                        </div>
                                                        <textarea value={step.content || ''} onChange={(e) => handleEditLessonFlow(idx, 'content', e.target.value)} className="w-full px-2 py-1 text-sm rounded border border-amber-200 focus:border-amber-400 outline-none resize-none" rows={2} placeholder="Student-facing instructions..." />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Materials */}
                                        <div className="bg-green-50 rounded-xl p-4">
                                            <h4 className="text-sm font-semibold text-green-700 mb-3">📦 Materials</h4>
                                            <textarea
                                                value={editedData.materials.join('\n')}
                                                onChange={(e) => handleEditMaterials(e.target.value)}
                                                className="w-full px-3 py-2 text-sm rounded-lg border border-green-200 focus:border-green-400 focus:ring-2 focus:ring-green-100 outline-none resize-none" rows={3}
                                                placeholder="One material per line..."
                                            />
                                        </div>

                                        {/* Differentiation & Assessment */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="bg-purple-50 rounded-xl p-4">
                                                <h4 className="text-sm font-semibold text-purple-700 mb-3">🔀 Differentiation</h4>
                                                <textarea value={editedData.differentiation} onChange={(e) => handleEditField('differentiation', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-purple-200 focus:border-purple-400 outline-none resize-none" rows={3} placeholder="Differentiation strategies..." />
                                            </div>
                                            <div className="bg-rose-50 rounded-xl p-4">
                                                <h4 className="text-sm font-semibold text-rose-700 mb-3">📊 Assessment</h4>
                                                <textarea value={editedData.assessment} onChange={(e) => handleEditField('assessment', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-rose-200 focus:border-rose-400 outline-none resize-none" rows={3} placeholder="Assessment strategy..." />
                                            </div>
                                        </div>
                                    </div>
                                ) : editedData ? (
                                    /* ===== Structured Read-Only View (click-to-edit inline) ===== */
                                    <div className="space-y-4">
                                        {/* Bell Ringer — with refresh affordance */}
                                        {editedData.bell_ringer && (
                                            <div className="bg-orange-50 rounded-xl p-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h4 className="text-sm font-semibold text-orange-700 flex items-center gap-2">
                                                        <Sparkles size={14} /> Bell Ringer
                                                    </h4>
                                                    <button
                                                        onClick={handleRefreshBellRinger}
                                                        disabled={refreshingBellRinger || isAnyActionRunning}
                                                        className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-orange-600 bg-orange-100 rounded-md hover:bg-orange-200 transition-colors disabled:opacity-50"
                                                        title="Generate a new bell ringer"
                                                    >
                                                        {refreshingBellRinger ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />}
                                                        {refreshingBellRinger ? 'Refreshing…' : 'New Bell Ringer'}
                                                    </button>
                                                </div>
                                                {inlineEditSection === 'bell_ringer' ? (
                                                    <textarea
                                                        value={typeof editedData.bell_ringer === 'string' ? editedData.bell_ringer : JSON.stringify(editedData.bell_ringer, null, 2)}
                                                        onChange={(e) => handleEditField('bell_ringer', e.target.value)}
                                                        onBlur={() => setInlineEditSection(null)}
                                                        autoFocus
                                                        className="w-full px-3 py-2 text-sm rounded-lg border border-orange-200 focus:border-orange-400 outline-none resize-none bg-white"
                                                        rows={3}
                                                    />
                                                ) : (
                                                    <p
                                                        className="text-sm text-orange-800 whitespace-pre-wrap cursor-pointer hover:bg-orange-100/50 rounded-lg p-1 -m-1 transition-colors"
                                                        onClick={() => setInlineEditSection('bell_ringer')}
                                                        title="Click to edit"
                                                    >
                                                        {typeof editedData.bell_ringer === 'string'
                                                            ? stripMarkdown(editedData.bell_ringer)
                                                            : editedData.bell_ringer.prompt || editedData.bell_ringer.question || JSON.stringify(editedData.bell_ringer)
                                                        }
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        {/* Objectives */}
                                        <div className="bg-blue-50 rounded-xl p-4">
                                            <h4 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-2">📌 Learning Objectives</h4>
                                            <ul className="space-y-1.5">
                                                {editedData.objectives?.map((obj, idx) => (
                                                    <li key={idx} className="text-sm text-blue-800 flex items-start gap-2">
                                                        <span className="text-blue-400 mt-0.5">•</span>
                                                        {inlineEditSection === `obj-${idx}` ? (
                                                            <input
                                                                type="text" value={obj}
                                                                onChange={(e) => handleEditObjective(idx, e.target.value)}
                                                                onBlur={() => setInlineEditSection(null)}
                                                                autoFocus
                                                                className="flex-1 px-2 py-1 text-sm rounded border border-blue-200 focus:border-blue-400 outline-none"
                                                            />
                                                        ) : (
                                                            <span
                                                                className="cursor-pointer hover:bg-blue-100/50 rounded px-1 -mx-1 transition-colors"
                                                                onClick={() => setInlineEditSection(`obj-${idx}`)}
                                                                title="Click to edit"
                                                            >
                                                                {stripMarkdown(obj)}
                                                            </span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Language Objectives — render only when present */}
                                        {editedData.language_objectives?.length > 0 && (
                                            <div className="bg-indigo-50 rounded-xl p-4">
                                                <h4 className="text-sm font-semibold text-indigo-700 mb-2 flex items-center gap-2">
                                                    <Globe size={14} /> Language Objectives
                                                </h4>
                                                <ul className="space-y-1.5">
                                                    {editedData.language_objectives.map((obj, idx) => (
                                                        <li key={idx} className="text-sm text-indigo-800 flex items-start gap-2">
                                                            <span className="text-indigo-400 mt-0.5">•</span>
                                                            <span>{stripMarkdown(typeof obj === 'string' ? obj : obj.text || obj.objective || '')}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {/* Lesson Flow — condensed */}
                                        <div className="bg-amber-50 rounded-xl p-4">
                                            <h4 className="text-sm font-semibold text-amber-700 mb-3">📋 Lesson Flow</h4>
                                            <div className="space-y-2">
                                                {editedData.lesson_flow?.map((step, idx) => {
                                                    const title = step.activity || step.title || step.phase?.replace(/_/g, ' ') || `Step ${idx + 1}`;
                                                    const rawTime = step.time || step.duration || '';
                                                    let timeDisplay = '';
                                                    if (rawTime) {
                                                        const rangeMatch = String(rawTime).match(/^(\d+)\s*-\s*(\d+)$/);
                                                        if (rangeMatch) {
                                                            timeDisplay = `${parseInt(rangeMatch[2]) - parseInt(rangeMatch[1])}m`;
                                                        } else {
                                                            const numStr = String(rawTime).replace(/\s*(min|minutes?)\.?\s*/gi, '').trim();
                                                            timeDisplay = /^\d+$/.test(numStr) ? `${numStr}m` : String(rawTime);
                                                        }
                                                    }
                                                    const contentText = step.content || step.student_facing || '';
                                                    return (
                                                        <div key={idx} className="bg-white rounded-lg p-3 border border-amber-200/60">
                                                            <div className="flex items-center gap-2">
                                                                <span className="bg-amber-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                                                                <span className="font-medium text-amber-800 text-sm">{title}</span>
                                                                {timeDisplay && <span className="text-[10px] text-amber-500 ml-auto bg-amber-100 px-1.5 py-0.5 rounded-full">{timeDisplay}</span>}
                                                            </div>
                                                            {contentText && contentText !== title && (
                                                                <div className="ml-7 mt-1 text-xs text-slate-600 leading-relaxed line-clamp-3">
                                                                    {normalizeBullets(contentText).slice(0, 3).map((line, i) => (
                                                                        <span key={i}>{line}{i < 2 ? ' · ' : ''}</span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Materials */}
                                        {editedData.materials?.length > 0 && (
                                            <div className="bg-emerald-50 rounded-xl p-4">
                                                <h4 className="text-sm font-semibold text-emerald-700 mb-2">📦 Materials</h4>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {editedData.materials.map((mat, idx) => (
                                                        <span key={idx} className="text-xs text-emerald-700 bg-emerald-100 px-2 py-1 rounded-md">{mat}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Differentiation & Assessment — compact row */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {editedData.differentiation && (
                                                <div className="bg-purple-50 rounded-xl p-4">
                                                    <h4 className="text-sm font-semibold text-purple-700 mb-1.5">🎯 Differentiation</h4>
                                                    {inlineEditSection === 'differentiation' ? (
                                                        <textarea
                                                            value={editedData.differentiation}
                                                            onChange={(e) => handleEditField('differentiation', e.target.value)}
                                                            onBlur={() => setInlineEditSection(null)}
                                                            autoFocus
                                                            className="w-full px-2 py-1.5 text-xs rounded-lg border border-purple-200 focus:border-purple-400 outline-none resize-none"
                                                            rows={3}
                                                        />
                                                    ) : (
                                                        <p
                                                            className="text-xs text-purple-800 line-clamp-4 cursor-pointer hover:bg-purple-100/50 rounded p-1 -m-1 transition-colors"
                                                            onClick={() => setInlineEditSection('differentiation')}
                                                            title="Click to edit"
                                                        >
                                                            {stripMarkdown(editedData.differentiation)}
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            {editedData.assessment && (
                                                <div className="bg-rose-50 rounded-xl p-4">
                                                    <h4 className="text-sm font-semibold text-rose-700 mb-1.5">📊 Assessment</h4>
                                                    {inlineEditSection === 'assessment' ? (
                                                        <textarea
                                                            value={editedData.assessment}
                                                            onChange={(e) => handleEditField('assessment', e.target.value)}
                                                            onBlur={() => setInlineEditSection(null)}
                                                            autoFocus
                                                            className="w-full px-2 py-1.5 text-xs rounded-lg border border-rose-200 focus:border-rose-400 outline-none resize-none"
                                                            rows={3}
                                                        />
                                                    ) : (
                                                        <p
                                                            className="text-xs text-rose-800 line-clamp-4 cursor-pointer hover:bg-rose-100/50 rounded p-1 -m-1 transition-colors"
                                                            onClick={() => setInlineEditSection('assessment')}
                                                            title="Click to edit"
                                                        >
                                                            {stripMarkdown(editedData.assessment)}
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Daily Agenda — simple teacher-facing/classroom-facing view */}
                                        <div className="border border-slate-200 rounded-xl overflow-hidden">
                                            <button
                                                onClick={() => setShowDailyAgenda(!showDailyAgenda)}
                                                className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
                                            >
                                                <span className="text-sm font-medium text-slate-700 flex items-center gap-2">
                                                    <Calendar size={14} /> Daily Agenda
                                                </span>
                                                <ChevronRight size={14} className={`text-slate-400 transition-transform ${showDailyAgenda ? 'rotate-90' : ''}`} />
                                            </button>
                                            {showDailyAgenda && editedData.lesson_flow && (
                                                <div className="p-4 bg-white border-t border-slate-200">
                                                    <p className="text-[10px] text-slate-400 mb-3 uppercase tracking-wide font-medium">Classroom-Ready Agenda</p>
                                                    <div className="space-y-2">
                                                        {editedData.lesson_flow.map((step, idx) => {
                                                            const title = step.activity || step.title || `Step ${idx + 1}`;
                                                            const rawTime = step.time || step.duration || '';
                                                            return (
                                                                <div key={idx} className="flex items-center gap-3 py-1.5 border-b border-slate-100 last:border-0">
                                                                    <span className="text-xs text-slate-400 w-12 flex-shrink-0 font-mono">{rawTime || `—`}</span>
                                                                    <span className="text-sm text-slate-700">{title}</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            const agendaText = editedData.lesson_flow.map((step, idx) => {
                                                                const title = step.activity || step.title || `Step ${idx + 1}`;
                                                                return `${step.time || ''} — ${title}`;
                                                            }).join('\n');
                                                            navigator.clipboard.writeText(agendaText);
                                                        }}
                                                        className="mt-3 flex items-center gap-1.5 text-xs text-slate-500 hover:text-amber-600 transition-colors"
                                                    >
                                                        <Copy size={12} /> Copy agenda
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    /* Fallback */
                                    <div className="bg-slate-50 rounded-xl p-6">
                                        <pre className="whitespace-pre-wrap font-mono text-sm text-slate-700 leading-relaxed">{lessonContent}</pre>
                                    </div>
                                )}
                            </div>

                            {/* Revision Panel */}
                            <div className="px-6 pb-6">
                                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
                                        <Edit3 size={16} /> Revise Lesson
                                    </h3>
                                    {revisionSuccess && (
                                        <div className="mb-3 p-2 bg-green-100 text-green-700 text-sm rounded-lg flex items-center gap-2">
                                            <Check size={16} /> Revision saved as v{currentVersion}!
                                        </div>
                                    )}
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={revisionFeedback}
                                            onChange={(e) => setRevisionFeedback(e.target.value)}
                                            placeholder="Make it shorter, add more practice, simplify vocabulary..."
                                            className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                                            disabled={isAnyActionRunning}
                                            onKeyDown={(e) => { if (e.key === 'Enter' && revisionFeedback.trim()) handleReviseLesson(); }}
                                        />
                                        <button
                                            onClick={handleReviseLesson}
                                            disabled={isAnyActionRunning || !revisionFeedback.trim()}
                                            className="px-4 py-2 bg-slate-600 text-white text-sm font-medium rounded-lg hover:bg-slate-700 transition-all disabled:opacity-50 flex items-center gap-2"
                                        >
                                            {revising ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
                                            {revising ? 'Revising...' : 'Revise'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ==================== RIGHT COLUMN (30%) ==================== */}
                    <div className="flex-[3] space-y-4 sticky top-24">
                        {/* Save-gate message */}
                        {!isSaved && (
                            <motion.div
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-center"
                            >
                                <p className="text-sm text-amber-700 font-medium">Save your lesson to unlock deliverables</p>
                                <button
                                    onClick={handleSaveLesson}
                                    disabled={savingLesson}
                                    className="mt-2 px-4 py-2 bg-[#FFC107] text-black rounded-lg text-sm font-medium hover:bg-[#e6ad00] transition-colors disabled:opacity-50 flex items-center gap-1.5 mx-auto"
                                >
                                    {savingLesson ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                                    {savingLesson ? 'Saving...' : 'Save Lesson'}
                                </button>
                            </motion.div>
                        )}

                        {/* Deliverables Panel */}
                        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-5">
                            <h3 className="text-sm font-semibold text-[#1d1d1f] mb-4">Build Deliverables</h3>
                            <div className="space-y-2.5">
                                <DeliverableButton
                                    onClick={handlePresentLesson}
                                    disabled={isAnyActionRunning}
                                    loading={generatingSlides}
                                    icon={Monitor}
                                    label="Create Slides"
                                    sublabel="Open in Classroom Board"
                                />
                                <DeliverableButton
                                    onClick={handleStudentPacket}
                                    disabled={isAnyActionRunning}
                                    loading={generatingPacket}
                                    icon={FileText}
                                    label="Create Student Copy"
                                    sublabel="Printable handouts"
                                />
                                <DeliverableButton
                                    onClick={handleGenerateQuiz}
                                    disabled={isAnyActionRunning && !quizGenerated}
                                    loading={generatingQuiz}
                                    icon={ClipboardCheck}
                                    label={quizGenerated ? 'Assessment Ready' : 'Create Assessment'}
                                    sublabel={quizGenerated ? 'View Quiz' : '10 questions + answer key'}
                                    success={!!quizGenerated}
                                />
                                <DeliverableButton
                                    onClick={handleCopyForCanvas}
                                    disabled={isAnyActionRunning}
                                    loading={exportingCanvas}
                                    icon={Copy}
                                    label={canvasSuccess ? 'Copied!' : 'Copy for Canvas'}
                                    sublabel="Canvas-ready HTML"
                                    success={canvasSuccess}
                                />
                                <DeliverableButton
                                    onClick={handleDownloadPptx}
                                    disabled={isAnyActionRunning}
                                    loading={downloadingPptx}
                                    icon={Download}
                                    label="Download PPT"
                                    sublabel="PowerPoint for offline"
                                />
                                <DeliverableButton
                                    onClick={handleGenerateVoiceover}
                                    disabled={isAnyActionRunning && !voiceoverReady}
                                    loading={generatingVoiceover}
                                    icon={Mic}
                                    label={voiceoverReady ? 'Voiceover Ready' : 'Generate Voiceover'}
                                    sublabel={voiceoverReady ? 'Play or download' : 'AI narration track'}
                                    success={voiceoverReady}
                                />
                            </div>

                            {/* Manage Voice Profile — always accessible */}
                            {isSaved && (
                                <button
                                    onClick={() => setShowVoiceSetupModal(true)}
                                    className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-slate-400 hover:text-amber-600 transition-colors rounded-lg hover:bg-amber-50/50"
                                >
                                    <Mic size={12} />
                                    Manage Voice Profile
                                </button>
                            )}
                        </div>

                        {/* Phase 3.1 — Contextual Lesson Chat (feature-flagged, user-aware) */}
                        {lessonChatEnabled && (
                            <LessonChatPanel
                                result={result}
                                editedData={editedData}
                                selectedSubject={selectedSubject}
                                gradeLevel={gradeLevel}
                                lessonDate={lessonDate}
                                userProfile={userProfile}
                            />
                        )}

                        {/* Phase 3A — Upgrade nudge for Free users (frequency-capped, after save only) */}
                        {isSaved && (userProfile?.plan_tier || 'Free').toLowerCase() === 'free' && (
                            <UpgradeNudge
                                nudgeType="post_lesson"
                                planTier={userProfile?.plan_tier || 'Free'}
                                featureArea="lesson"
                                compact={true}
                                onUpgrade={(source) => {
                                    setPricingSource(source);
                                    setShowPricing(true);
                                }}
                            />
                        )}

                        {/* Continue Planning CTA */}
                        {isSaved && (
                            <motion.button
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                onClick={onPlanNextDay}
                                className="w-full py-3 bg-gradient-to-r from-amber-400 to-amber-500 text-black rounded-xl font-medium text-sm hover:from-amber-500 hover:to-amber-600 active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2"
                            >
                                Continue Planning
                                <ArrowRight size={16} />
                            </motion.button>
                        )}
                    </div>
                </div>
            </div>

            {/* Quiz Modal */}
            <QuizModal
                quiz={quizGenerated}
                isOpen={showQuizModal}
                onClose={() => setShowQuizModal(false)}
                onSave={async (editedQuiz) => {
                    // Update in-session state
                    setQuizGenerated(editedQuiz);
                    // Persist to Supabase if we have a saved document
                    if (savedDocumentId && userProfile?.email) {
                        await api.put(`/api/documents/${savedDocumentId}/content`, {
                            teacher_id: userProfile.email,
                            content: JSON.stringify({
                                title: editedQuiz.title,
                                questions: editedQuiz.questions,
                                total_points: editedQuiz.total_points,
                                grade_level: editedQuiz.grade_level,
                            }),
                            title: editedQuiz.title,
                            metadata: {
                                num_questions: editedQuiz.questions?.length,
                                total_points: editedQuiz.total_points,
                                last_edited: new Date().toISOString(),
                            }
                        });
                    }
                }}
            />

            {/* Voice Setup Modal */}
            <VoiceSetupModal
                isOpen={showVoiceSetupModal}
                onClose={() => setShowVoiceSetupModal(false)}
                teacherId={userProfile?.email}
                teacherName={userProfile?.name}
                onVoiceCreated={() => {
                    setShowVoiceSetupModal(false);
                    // After voice is created, start voiceover flow
                    setVoiceoverAsset(null);
                    setShowVoiceoverModal(true);
                }}
            />

            {/* Voiceover Modal */}
            <VoiceoverModal
                isOpen={showVoiceoverModal}
                onClose={() => {
                    setShowVoiceoverModal(false);
                    // Refresh voiceover state after modal closes
                    if (savedDocumentId && userProfile?.email) {
                        voiceover.getVoiceover(savedDocumentId, userProfile.email)
                            .then(res => {
                                if (res.data?.asset) {
                                    setVoiceoverAsset(res.data.asset);
                                    setVoiceoverReady(true);
                                }
                            })
                            .catch(() => {});
                    }
                }}
                teacherId={userProfile?.email}
                lessonDocumentId={savedDocumentId}
                lessonTitle={lessonTitle}
                existingAsset={voiceoverAsset}
            />

            {/* Pricing Page (Phase 3A) */}
            {showPricing && (
                <PricingPage
                    onClose={() => setShowPricing(false)}
                    currentPlan={userProfile?.plan_tier || 'Free'}
                    source={pricingSource}
                />
            )}
        </motion.div>
    );
};

export default PostGenerationView;
